import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import * as rbd from 'react-beautiful-dnd';
import { useIntl } from 'umi';
import { Button, Modal } from 'tea-component';
import ShortcutEditItem from './components/ShortcutEditItem';
import ShortcutEditAddButton from './components/ShortcutEditAddButton';
import { getUrlCollectionItemKey, reindexUrlCollectionItems } from '@home/utils/urlCollectionData';
import {
  applyPendingShortcutRename,
  getDuplicateShortcutNameKey,
  getShortcutItemsValidationError,
  hasShortcutItemsChanges,
  SHORTCUT_NAME_MAX_LENGTH,
} from '../../utils/shortcutRules';

import type { IUrlCollectionItem } from '@home/types/urlCollection';

import './index.scss';

interface IEditShortcutsModalProps extends IBaseProps {
  visible: boolean;
  items: IUrlCollectionItem[];
  initialItems?: IUrlCollectionItem[];
  title?: string;
  shortcutsTitle?: string;
  addText?: string;
  confirmText?: string;
  cancelText?: string;
  maxCount?: number;
  loading?: boolean;
  disabledClose?: boolean;
  maxCountMessage?: string;
  nameEmptyMessage?: string;
  nameTooLongMessage?: string;
  duplicateNameMessage?: string;
  duplicateUrlMessage?: string;
  onAdd?: () => void;
  onCancel: () => void;
  onItemsChange?: (items: IUrlCollectionItem[]) => void;
  onItemClick?: (item: IUrlCollectionItem) => void;
  onConfirm: (items: IUrlCollectionItem[]) => void;
}

const EditShortcutsModal: React.FC<IEditShortcutsModalProps> = (props) => {
  const {
    className,
    style,
    visible,
    items,
    initialItems: initialItemsProp,
    title,
    shortcutsTitle,
    addText,
    confirmText,
    cancelText,
    maxCount = 6,
    loading = false,
    disabledClose = false,
    maxCountMessage,
    nameEmptyMessage,
    nameTooLongMessage,
    duplicateNameMessage,
    duplicateUrlMessage,
    onAdd,
    onCancel,
    onItemsChange,
    onItemClick,
    onConfirm,
  } = props;
  const { formatMessage } = useIntl();
  const titleText =
    title ||
    formatMessage({
      id: 'dbh_url_collection_edit_modal_title',
      defaultMessage: 'Edit My Shortcuts',
    });
  const shortcutsTitleText =
    shortcutsTitle ||
    formatMessage({
      id: 'dbh_url_collection_shortcuts_title',
      defaultMessage: 'My Shortcuts',
    });
  const addTextValue =
    addText ||
    formatMessage({
      id: 'dbh_url_collection_add_shortcut',
      defaultMessage: 'Add Shortcut',
    });
  const confirmTextValue =
    confirmText ||
    formatMessage({
      id: 'dbh_confirm',
      defaultMessage: 'Confirm',
    });
  const cancelTextValue =
    cancelText ||
    formatMessage({
      id: 'dbh_cancel',
      defaultMessage: 'Cancel',
    });

  const [editingKey, setEditingKey] = useState('');
  const [editingName, setEditingName] = useState('');
  const [nameErrorKey, setNameErrorKey] = useState('');
  const [nameErrorText, setNameErrorText] = useState('');
  const skipNextRenameBlurRef = useRef(false);
  const draftItems = items || [];
  const initialItems = initialItemsProp || draftItems;
  const isActionDisabled = loading;
  const pendingRenamedItems = applyPendingShortcutRename(draftItems, editingKey, editingName);
  const isEditingNameEmpty = !!editingKey && !editingName.trim();
  const isEditingNameTooLong = !!editingKey && editingName.trim().length > SHORTCUT_NAME_MAX_LENGTH;
  const isEditingNameDuplicated = !!editingKey && !!getDuplicateShortcutNameKey(draftItems, editingName, editingKey);

  const syncItemsChange = (nextItems: IUrlCollectionItem[]): void => {
    if (onItemsChange) {
      onItemsChange(nextItems);
    }
  };

  const handleCancel = (): void => {
    if (disabledClose) {
      return;
    }

    setEditingKey('');
    setEditingName('');
    setNameErrorKey('');
    setNameErrorText('');
    onCancel();
  };

  const getNameValidationMessage = (type: string): string => {
    if (type === 'long_name') {
      return (
        nameTooLongMessage ||
        formatMessage({
          id: 'dbh_url_collection_name_too_long',
          defaultMessage: 'Name must be 80 characters or fewer.',
        })
      );
    }

    if (type === 'duplicate_name') {
      return (
        duplicateNameMessage ||
        formatMessage({
          id: 'dbh_url_collection_duplicate_name',
          defaultMessage: 'This name has already been used.',
        })
      );
    }

    if (type === 'duplicate_url') {
      return (
        duplicateUrlMessage ||
        formatMessage({
          id: 'dbh_url_collection_duplicate_url',
          defaultMessage: 'This page has already been added.',
        })
      );
    }

    return (
      nameEmptyMessage ||
      formatMessage({
        id: 'dbh_url_collection_name_required',
        defaultMessage: 'Name is required.',
      })
    );
  };

  const setNameValidationError = (key: string, type: string): void => {
    setNameErrorKey(key);
    setNameErrorText(getNameValidationMessage(type));
  };

  const focusShortcutValidationError = (
    nextItems: IUrlCollectionItem[],
    validationError: ReturnType<typeof getShortcutItemsValidationError>,
  ): boolean => {
    if (!validationError.key) {
      return false;
    }

    const errorItem = nextItems.find((item) => getUrlCollectionItemKey(item) === validationError.key);

    if (errorItem) {
      setEditingKey(validationError.key);
      setEditingName(errorItem.name);
    }
    setNameValidationError(validationError.key, validationError.type);
    return true;
  };

  const handleConfirm = (): void => {
    if (isActionDisabled) {
      return;
    }

    if (editingKey) {
      const nextName = editingName.trim();

      if (!nextName) {
        setNameValidationError(editingKey, 'empty_name');
        return;
      }

      if (nextName.length > SHORTCUT_NAME_MAX_LENGTH) {
        setNameValidationError(editingKey, 'long_name');
        return;
      }

      if (getDuplicateShortcutNameKey(draftItems, nextName, editingKey)) {
        setNameValidationError(editingKey, 'duplicate_name');
        return;
      }

      const nextItems = reindexUrlCollectionItems(applyPendingShortcutRename(draftItems, editingKey, editingName));
      const validationError = getShortcutItemsValidationError(nextItems);

      if (focusShortcutValidationError(nextItems, validationError)) {
        return;
      }

      syncItemsChange(nextItems);
      setEditingKey('');
      setEditingName('');
      setNameErrorKey('');
      setNameErrorText('');
      onConfirm(nextItems);
      return;
    }

    const validationError = getShortcutItemsValidationError(draftItems);

    if (focusShortcutValidationError(draftItems, validationError)) {
      return;
    }

    onConfirm(reindexUrlCollectionItems(draftItems));
  };

  const handleRemove = (key: string): void => {
    if (isActionDisabled) {
      return;
    }

    const nextItems = reindexUrlCollectionItems(draftItems.filter((item) => getUrlCollectionItemKey(item) !== key));

    if (editingKey === key) {
      setEditingKey('');
      setEditingName('');
      setNameErrorKey('');
      setNameErrorText('');
    }
    syncItemsChange(nextItems);
  };

  const handleRemoveClick = (item: IUrlCollectionItem): void => {
    if (isActionDisabled) {
      return;
    }

    Modal.confirm({
      maskStyle: { zIndex: 1001 },
      description: formatMessage({
        id: 'dbh_url_collection_delete_confirm_title',
        defaultMessage: 'Are you sure you want to delete it?',
      }),
      okText: formatMessage({ id: 'dbh_confirm' }),
      cancelText: formatMessage({ id: 'dbh_cancel' }),
      onOk: () => {
        handleRemove(getUrlCollectionItemKey(item));
      },
    });
  };

  const startRename = (item: IUrlCollectionItem): void => {
    if (isActionDisabled) {
      return;
    }

    const itemKey = getUrlCollectionItemKey(item);
    skipNextRenameBlurRef.current = false;
    setEditingKey(itemKey);
    setEditingName(item.name);
    setNameErrorKey('');
    setNameErrorText('');
  };

  const finishRename = (): void => {
    if (skipNextRenameBlurRef.current) {
      skipNextRenameBlurRef.current = false;
      return;
    }

    if (isActionDisabled) {
      return;
    }

    if (!editingKey) {
      return;
    }

    const nextName = editingName.trim();

    if (!nextName) {
      setNameValidationError(editingKey, 'empty_name');
      return;
    }

    if (nextName.length > SHORTCUT_NAME_MAX_LENGTH) {
      setNameValidationError(editingKey, 'long_name');
      return;
    }

    if (getDuplicateShortcutNameKey(draftItems, nextName, editingKey)) {
      setNameValidationError(editingKey, 'duplicate_name');
      return;
    }

    const nextItems = draftItems.map((item) => {
      if (getUrlCollectionItemKey(item) !== editingKey) {
        return item;
      }

      return {
        ...item,
        name: nextName,
      };
    });

    syncItemsChange(nextItems);
    setEditingKey('');
    setEditingName('');
    setNameErrorKey('');
    setNameErrorText('');
  };

  const cancelRename = (): void => {
    skipNextRenameBlurRef.current = true;
    setEditingKey('');
    setEditingName('');
    setNameErrorKey('');
    setNameErrorText('');
  };

  const handleDragEnd = (result: rbd.DropResult): void => {
    if (isActionDisabled) {
      return;
    }

    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (source.index === destination.index) {
      return;
    }

    const nextItems = [...draftItems];
    const [movedItem] = nextItems.splice(source.index, 1);
    if (!movedItem) {
      return;
    }

    nextItems.splice(destination.index, 0, movedItem);
    syncItemsChange(reindexUrlCollectionItems(nextItems));
  };

  const handleAdd = (): void => {
    if (isActionDisabled) {
      return;
    }

    onAdd?.();
  };

  const countText = `${shortcutsTitleText} (${draftItems.length}/${maxCount})`;
  const showAddButton = draftItems.length < maxCount;
  const shortcutItemsValidationError = getShortcutItemsValidationError(pendingRenamedItems);
  const disabledConfirm =
    isActionDisabled ||
    isEditingNameEmpty ||
    isEditingNameTooLong ||
    isEditingNameDuplicated ||
    (!shortcutItemsValidationError.key && !hasShortcutItemsChanges(initialItems, pendingRenamedItems));
  const maxCountTips =
    maxCountMessage ||
    formatMessage({
      id: 'dbh_url_collection_max_count_message',
      defaultMessage: 'Maximum limit of 6 favorites reached. Remove an item to add a new one.',
    });
  const renameTooltipText = formatMessage({
    id: 'dbh_url_collection_rename',
    defaultMessage: 'Rename',
  });
  const deleteTooltipText = formatMessage({
    id: 'dbh_url_collection_delete',
    defaultMessage: 'Delete',
  });
  const emptyNameTips =
    nameEmptyMessage ||
    formatMessage({
      id: 'dbh_url_collection_name_required',
      defaultMessage: 'Name is required.',
    });
  let currentNameErrorText = nameErrorText;

  if (isEditingNameTooLong) {
    currentNameErrorText = getNameValidationMessage('long_name');
  } else if (isEditingNameDuplicated) {
    currentNameErrorText = getNameValidationMessage('duplicate_name');
  }

  const currentNameErrorKey = isEditingNameTooLong || isEditingNameDuplicated ? editingKey : nameErrorKey;

  return (
    <Modal
      style={style}
      className={classnames('dbh-my-shortcuts-and-new-features-card-common-modal dbh-edit-shortcuts-modal', className, {
        'dbh-edit-shortcuts-modal--empty': !items?.length,
      })}
      size={600}
      visible={visible}
      caption={titleText}
      disableEscape={disabledClose}
      disableCloseIcon={disabledClose}
      onClose={handleCancel}
    >
      <Modal.Body>
        <div className="dbh-edit-shortcuts-modal__summary">{countText}</div>
        <div className="dbh-edit-shortcuts-modal__list">
          <rbd.DragDropContext onDragEnd={handleDragEnd}>
            <rbd.Droppable droppableId="dbh-edit-shortcuts-modal-list">
              {(provided: any, snapshot: any) => {
                if (!items?.length) {
                  return null;
                }

                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={classnames('dbh-edit-shortcuts-modal__droppable', {
                      'dbh-edit-shortcuts-modal__droppable--dragging-over': snapshot.isDraggingOver,
                    })}
                  >
                    {draftItems?.map((item, index) => {
                      const itemKey = getUrlCollectionItemKey(item);

                      return (
                        <rbd.Draggable
                          key={itemKey}
                          draggableId={itemKey}
                          index={index}
                          isDragDisabled={isActionDisabled || editingKey === itemKey}
                        >
                          {(dragProvided: any, dragSnapshot: any) => {
                            return (
                              <ShortcutEditItem
                                item={item}
                                editingKey={editingKey}
                                editingName={editingName}
                                nameErrorKey={currentNameErrorKey}
                                nameErrorText={currentNameErrorText || emptyNameTips}
                                disabledAction={isActionDisabled}
                                isDragging={dragSnapshot.isDragging}
                                draggableProps={dragProvided.draggableProps}
                                dragHandleProps={dragProvided.dragHandleProps}
                                innerRef={dragProvided.innerRef}
                                renameTooltipText={renameTooltipText}
                                deleteTooltipText={deleteTooltipText}
                                formatMessage={formatMessage}
                                setEditingName={setEditingName}
                                setNameErrorKey={setNameErrorKey}
                                setNameErrorText={setNameErrorText}
                                startRename={startRename}
                                finishRename={finishRename}
                                cancelRename={cancelRename}
                                handleRemoveClick={handleRemoveClick}
                                handleItemClick={onItemClick}
                              />
                            );
                          }}
                        </rbd.Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                );
              }}
            </rbd.Droppable>
          </rbd.DragDropContext>
          {showAddButton && (
            <ShortcutEditAddButton addText={addTextValue} disabled={isActionDisabled} onAdd={handleAdd} />
          )}
        </div>
        {!showAddButton && <div className="dbh-edit-shortcuts-modal__max-count-tips">{maxCountTips}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" loading={loading} disabled={disabledConfirm} onClick={handleConfirm}>
          {confirmTextValue}
        </Button>
        <Button type="weak" disabled={disabledClose} onClick={handleCancel}>
          {cancelTextValue}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditShortcutsModal;
